import React from "react";
import { cn } from "../../lib/utils";
import { Button } from "../atoms/button";
import { Eyebrow } from "../atoms/eyebrow";
import { Kbd } from "../atoms/kbd";
import { PanelHeader } from "../molecules/panel-header";
import { EmptyState } from "../molecules/empty-state";

interface OutputPanelProps {
	output: string;
	error: string;
	plotImages?: string[];
	isReady: boolean;
	onClear: () => void;
	title?: React.ReactNode;
	fontSize?: number;
	isExpanded?: boolean;
	onToggleExpand?: () => void;
	isHidden?: boolean;
	onToggleHidden?: () => void;
}

const OutputPanel: React.FC<OutputPanelProps> = ({
	output,
	error,
	plotImages = [],
	isReady,
	onClear,
	title = "Console",
	fontSize = 12,
	isExpanded = false,
	onToggleExpand,
	isHidden = false,
	onToggleHidden,
}) => {
	const hasContent = output || error || plotImages.length > 0;

	if (isHidden) {
		return (
			<div className="bg-white/95 dark:bg-[#0c0c0e]/95 border-t border-gray-200/80 dark:border-white/[0.06] animate-fade-in transition-colors duration-200">
				<button
					type="button"
					onClick={onToggleHidden}
					className="w-full px-3.5 py-2 cursor-pointer hover:bg-gray-50/80 dark:hover:bg-white/[0.03] transition-colors duration-150 flex items-center justify-between"
				>
					<div className="flex items-baseline gap-2">
						<Eyebrow className="!text-[9px]">Console</Eyebrow>
						{hasContent && (
							<div className="flex items-center gap-1 text-[10px] tabular text-emerald-600 dark:text-emerald-400">
								<span className="inline-block w-1 h-1 bg-emerald-500 rounded-full" />
								output
							</div>
						)}
					</div>
					<span className="text-[10px] text-gray-500 dark:text-gray-500 tracking-tight">
						Click to show ↑
					</span>
				</button>
			</div>
		);
	}

	return (
		<div className="bg-white/95 dark:bg-[#0c0c0e]/95 border-t border-gray-200/80 dark:border-white/[0.06] flex flex-col h-full overflow-hidden animate-fade-in transition-colors duration-200">
			<PanelHeader
				eyebrow="Console"
				heading={title}
				meta={
					hasContent ? (
						<span className="inline-block w-1 h-1 bg-emerald-500 rounded-full" />
					) : undefined
				}
				className="!py-2"
				actions={
					<>
						{onToggleHidden && (
							<Button
								variant="ghost"
								size="xs"
								onClick={onToggleHidden}
								title={isHidden ? "Show console" : "Hide console"}
							>
								{isHidden ? "Show" : "Hide"}
							</Button>
						)}
						{onToggleExpand && (
							<Button
								variant="ghost"
								size="xs"
								onClick={onToggleExpand}
								title={isExpanded ? "Shrink" : "Expand"}
							>
								{isExpanded ? "Shrink" : "Expand"}
							</Button>
						)}
						{hasContent && (
							<Button
								variant="ghost"
								size="xs"
								onClick={onClear}
								className="hover:!text-red-600 dark:hover:!text-red-400"
							>
								Clear
							</Button>
						)}
					</>
				}
			/>

			<div
				className="flex-1 bg-[#fbfbf7] dark:bg-[#0a0a0b] text-gray-800 dark:text-gray-200 px-4 py-3 overflow-auto relative cursor-text"
				style={{
					fontFamily:
						"'Geist Mono', 'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
					fontSize: `${fontSize}px`,
					lineHeight: 1.6,
				}}
			>
				<div className="relative z-10">
					{!isReady ? (
						<div className="flex flex-col items-center justify-center h-full text-gray-500 py-6">
							<div className="relative mb-3">
								<div className="animate-spin w-4 h-4 border-2 border-gray-300 dark:border-white/[0.1] border-t-orange-500 dark:border-t-amber-400 rounded-full" />
							</div>
							<div className="text-center">
								<Eyebrow className="!text-[11px] !text-gray-600 dark:!text-gray-400 mb-1 block">
									Loading Pyodide
								</Eyebrow>
								<p className="text-[10px] text-gray-400 dark:text-gray-600 tracking-tight">
									Setting up the Python runtime…
								</p>
							</div>
						</div>
					) : !hasContent ? (
						<EmptyState
							className="h-full !py-0"
							heading="Ready to run."
							description={
								<>
									Press <Kbd className="mx-0.5">⌘</Kbd>
									<Kbd className="mx-0.5">↵</Kbd> or click{" "}
									<span className="text-emerald-700 dark:text-emerald-400 font-medium">
										Run
									</span>{" "}
									to execute your code.
								</>
							}
						/>
					) : (
						<div className="space-y-3">
							{error && (
								<div className="text-red-600 dark:text-red-400 whitespace-pre-wrap border-l-2 border-red-500 pl-3">
									{error}
								</div>
							)}

							{output && (
								<div className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
									{output}
								</div>
							)}

							{plotImages.length > 0 && (
								<div className="pt-2 border-t border-gray-200 dark:border-white/[0.06]">
									<div className="flex items-center gap-2 mb-2">
										<Eyebrow className="!text-[9px]">
											Plots · {plotImages.length}
										</Eyebrow>
									</div>
									<div className="space-y-3">
										{plotImages.map((imageData, index) => (
											<figure
												key={index}
												className={cn(
													"bg-white dark:bg-[#131316] p-3 rounded-md",
													"border border-gray-200 dark:border-white/[0.06]",
												)}
											>
												<figcaption className="flex items-center justify-between mb-2">
													<span className="text-[11px] font-medium text-gray-600 dark:text-gray-400 tracking-tight">
														Plot {index + 1}
													</span>
													<a
														href={imageData}
														download={`plot_${index + 1}.png`}
														className="text-[10px] uppercase tracking-[0.14em] text-orange-600 dark:text-amber-400 hover:underline"
													>
														Download
													</a>
												</figcaption>
												<img
													src={imageData}
													alt={`Plot ${index + 1}`}
													className="max-w-full h-auto rounded border border-gray-200 dark:border-white/[0.06]"
													style={{ maxHeight: "300px" }}
												/>
											</figure>
										))}
									</div>
								</div>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default OutputPanel;
